import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { Setting } from '@common/interfaces/setting.interface';
import { SettingKeyCodes } from '@common/known-types/setting-key.codes';
import { User } from '@common/models/user';
import { CommonService } from '@common/services/common.service';
import { WebsocketService } from '@common/services/websocket.service';
import { tapSuccessResult } from '@ngneat/query';
import { SplitterComponent } from '@progress/kendo-angular-layout';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-main-layout',
    templateUrl: './main-layout.component.html'
})
export class MainLayoutComponent implements OnInit, OnDestroy {
    @ViewChild('consoleSplitter', { static: false }) consoleSplitter: SplitterComponent;

    isAuthenticated = false;
    backgroundImageUrl: SafeUrl;
    currentUser: User;

    private destroy$ = new Subject<boolean>();

    constructor(
        private websocketService: WebsocketService,
        public commonService: CommonService
    ) {}

    ngOnInit(): void {
        this.commonService.userService.isAuthenticatedSubject
            .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe((value) => {
                this.isAuthenticated = value;
                if (this.isAuthenticated) this.getSettings();
                else this.getLoginSettings();
            });

        this.commonService.userService.currentUserSubject.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.currentUser = value;
            if (this.currentUser)
                try {
                    this.websocketService.info('Client connected');
                    this.commonService.i18nService.setLanguageFromUser(this.currentUser);
                } catch {
                    /* empty */
                }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    private handleImageUpload(fileContent: string) {
        const decodedData = atob(fileContent);
        const uint8Array = new Uint8Array(decodedData.length);
        for (let i = 0; i < decodedData.length; ++i) {
            uint8Array[i] = decodedData.charCodeAt(i);
        }
        const blob = new Blob([uint8Array], { type: 'image/jpeg' });
        this.backgroundImageUrl = URL.createObjectURL(blob);
    }

    private getSettings() {
        this.commonService.settingsService
            .getSettings()
            .pipe(
                takeUntil(this.destroy$),
                tapSuccessResult((result: Setting[]) => {
                    const appBackgroundImageContent = result.find(
                        (setting: Setting) => setting.key === SettingKeyCodes.AppBackgroundImage
                    )?.value;
                    if (appBackgroundImageContent) this.handleImageUpload(appBackgroundImageContent);
                    else this.backgroundImageUrl = null;
                })
            )
            .subscribe();
    }

    private getLoginSettings() {
        this.commonService.settingsService
            .getLoginSettings()
            .pipe(
                takeUntil(this.destroy$),
                tapSuccessResult((result: Setting[]) => {
                    const loginBackgroundImageContent = result.find(
                        (setting: Setting) => setting.key === SettingKeyCodes.LoginBackgroundImage
                    )?.value;
                    if (loginBackgroundImageContent) this.handleImageUpload(loginBackgroundImageContent);
                    else this.backgroundImageUrl = null;
                })
            )
            .subscribe();
    }
}
